import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Search, ChevronLeft, ChevronRight, Users, MapPin, Building2,
  Phone, Mail, User, Send, Loader2, ZoomIn, ZoomOut, RotateCcw,
  Calendar, Globe
} from 'lucide-react';

const CELL_SIZE = 20;

// Match exact colors used by the internal stand editor (useCanvasDrawing.js)
const STATUS_FILL = {
  'müsait':  '#22c55e',
  'rezerve': '#f59e0b',
  'satıldı': '#ef4444',
  'dolu':    '#94a3b8',
};

const STATUS_BORDER = {
  'müsait':  '#16a34a',
  'rezerve': '#d97706',
  'satıldı': '#dc2626',
  'dolu':    '#64748b',
};

// For legend chips
const STATUS_LABEL = { müsait: 'Müsait', rezerve: 'Rezerve', satıldı: 'Satıldı' };
const STATUS_BADGE_CLS = {
  rezerve: 'bg-amber-100 text-amber-800 border-amber-200',
  satıldı: 'bg-red-100 text-red-800 border-red-200',
};

function formatDate(str) {
  if (!str) return '';
  try {
    const d = new Date(str);
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return str; }
}

// ── Stand shape rendered on SVG canvas ───────────────────────────────
function StandShape({ stand, scale, onClick, highlighted }) {
  const fillColor  = highlighted ? '#0ea5e9' : (stand.color || STATUS_FILL[stand.status] || STATUS_FILL['müsait']);
  const borderColor = highlighted ? '#0284c7' : STATUS_BORDER[stand.status] || STATUS_BORDER['müsait'];
  const available  = stand.status === 'müsait';
  const sw = stand.width  * CELL_SIZE * scale;
  const sh = stand.height * CELL_SIZE * scale;
  const sx = stand.x      * CELL_SIZE * scale;
  const sy = stand.y      * CELL_SIZE * scale;
  const area = stand.width * stand.height;
  const companyLabel = (stand.stand_name || stand.assigned_company_name || '');

  return (
    <g
      transform={`translate(${sx},${sy})`}
      onClick={available ? () => onClick(stand) : undefined}
      style={{ cursor: available ? 'pointer' : 'default' }}
      data-testid={`stand-${stand.id}`}
    >
      <rect
        width={sw} height={sh}
        rx={2 * scale}
        fill={fillColor}
        stroke={borderColor}
        strokeWidth={highlighted ? 2.5 * scale : 1.5 * scale}
        opacity={stand.opacity ?? 1}
        style={{ transition: 'all 0.15s' }}
      />

      {/* Hover overlay for available stands */}
      {available && (
        <rect
          width={sw} height={sh}
          rx={2 * scale}
          fill="white"
          opacity={0}
          style={{ transition: 'opacity 0.15s' }}
          className="hover:opacity-20"
        />
      )}

      {/* Stand number — top-right corner, white text */}
      {sw > 16 * scale && sh > 12 * scale && stand.stand_number && (
        <text
          x={sw - 3 * scale}
          y={5 * scale}
          textAnchor="end"
          dominantBaseline="hanging"
          fontSize={Math.max(7, Math.min(10, sw / 6)) * scale}
          fontWeight="700"
          fill="white"
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          {stand.stand_number}
        </text>
      )}

      {/* Company name — center, white italic */}
      {companyLabel && sw > 24 * scale && sh > 18 * scale && (
        <text
          x={sw / 2}
          y={sh / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={Math.max(6, Math.min(9, sw / 7)) * scale}
          fill="white"
          fontStyle="italic"
          fontWeight="500"
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          {companyLabel.slice(0, 14)}
        </text>
      )}

      {/* m² area — bottom-left, white */}
      {stand.show_area && area > 0 && sw > 24 * scale && sh > 20 * scale && (
        <text
          x={3 * scale}
          y={sh - 3 * scale}
          textAnchor="start"
          dominantBaseline="auto"
          fontSize={Math.max(6, Math.min(8, sw / 8)) * scale}
          fill="white"
          opacity={0.85}
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          {area}m²
        </text>
      )}
    </g>
  );
}

// ── Main component ───────────────────────────────────────────────────
export default function ExpoMap() {
  const { exhibitionId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Map state
  const [selectedHall, setSelectedHall] = useState(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const svgRef = useRef(null);

  // Left panel
  const [participantSearch, setParticipantSearch] = useState('');
  const [panelOpen, setPanelOpen] = useState(true);
  const [highlightedStandId, setHighlightedStandId] = useState(null);

  // Participant detail modal
  const [selectedParticipant, setSelectedParticipant] = useState(null);

  // Request modal
  const [requestStand, setRequestStand] = useState(null);
  const [requestForm, setRequestForm] = useState({ contact_name: '', contact_company: '', contact_email: '', contact_phone: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/expo/${exhibitionId}/public`)
      .then(r => {
        setData(r.data);
        if (r.data.halls?.length > 0) setSelectedHall(r.data.halls[0].id);
        setLoading(false);
      })
      .catch(e => {
        setError(e.response?.data?.detail || 'Fuar bulunamadı');
        setLoading(false);
      });
  }, [exhibitionId]);

  const hallStands = (data?.stands || []).filter(s => !selectedHall || s.hall_id === selectedHall);

  // Canvas bounds
  const maxX = hallStands.reduce((m, s) => Math.max(m, s.x + s.width), 40);
  const maxY = hallStands.reduce((m, s) => Math.max(m, s.y + s.height), 30);
  const canvasW = maxX * CELL_SIZE;
  const canvasH = maxY * CELL_SIZE;

  // Drag to pan
  const onMouseDown = (e) => { if (e.button === 0) { setIsDragging(true); setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y }); } };
  const onMouseMove = useCallback((e) => { if (isDragging && dragStart) setOffset({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y }); }, [isDragging, dragStart]);
  const onMouseUp = () => { setIsDragging(false); setDragStart(null); };
  const onWheel = (e) => { e.preventDefault(); const delta = e.deltaY > 0 ? 0.9 : 1.1; setScale(s => Math.max(0.3, Math.min(4, s * delta))); };

  const filteredParticipants = (data?.participants || []).filter(p =>
    !participantSearch || p.company_name?.toLowerCase().includes(participantSearch.toLowerCase())
  );

  const handleStandClick = (stand) => {
    setRequestStand(stand);
    setRequestForm({ contact_name: '', contact_company: '', contact_email: '', contact_phone: '', notes: '' });
  };

  const handleParticipantClick = (p) => {
    setSelectedParticipant(p);
    setHighlightedStandId(p.stand_id);
  };

  const handleSubmitRequest = async () => {
    if (!requestForm.contact_name || !requestForm.contact_email || !requestForm.contact_company) {
      toast.error('Ad, şirket ve e-posta zorunludur'); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/expo/stand-requests`, {
        exhibition_id: exhibitionId,
        stand_id: requestStand.id,
        ...requestForm,
      });
      toast.success('Talebiniz alındı! Organizatör size dönecektir.');
      setRequestStand(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Talep gönderilemedi');
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-600 font-medium">{error}</p>
      </div>
    </div>
  );

  const ex = data.exhibition;

  // Stand counts per status (hall-specific)
  const statusCounts = { müsait: 0, rezerve: 0, satıldı: 0 };
  hallStands.forEach(s => { if (s.status in statusCounts) statusCounts[s.status]++; });

  // Build meta info for header
  const metaParts = [];
  if (ex.city) metaParts.push(ex.city);
  if (ex.country) metaParts.push(ex.country);
  const dateStr = ex.start_date
    ? ex.end_date
      ? `${formatDate(ex.start_date)} – ${formatDate(ex.end_date)}`
      : formatDate(ex.start_date)
    : '';
  if (dateStr) metaParts.push(dateStr);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="expo-map-page">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200 px-4 py-2.5 sticky top-0 z-30">
        {/* Top row: logo | fuar adı (orta) | legend (sağ) */}
        <div className="flex items-center justify-between gap-4">
          {/* Left: Logo */}
          <div className="shrink-0 w-32">
            {data.organizer_logo ? (
              <img
                src={data.organizer_logo}
                alt={data.organizer_name || 'Organizatör'}
                className="h-8 w-auto max-w-[120px] object-contain"
                onError={e => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div className="w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center">
                <MapPin className="w-4 h-4 text-white" />
              </div>
            )}
          </div>

          {/* Center: Exhibition name + meta */}
          <div className="flex-1 text-center">
            <h1 className="text-base font-bold text-gray-900">{ex.name || 'Fuar Haritası'}</h1>
            {metaParts.length > 0 && (
              <p className="text-xs text-gray-500 mt-0.5">
                {metaParts.map((p, i) => (
                  <span key={i}>
                    {i > 0 && <span className="mx-1.5 text-gray-300">|</span>}
                    {p}
                  </span>
                ))}
              </p>
            )}
          </div>

          {/* Right: Legend with counts */}
          <div className="shrink-0 w-32 flex flex-col items-end gap-0.5">
            {Object.entries(STATUS_LABEL).map(([status, label]) => (
              <span key={status} className="flex items-center gap-1 text-xs text-gray-600">
                <span className="w-2.5 h-2.5 rounded-sm inline-block border shrink-0"
                  style={{ background: STATUS_FILL[status], borderColor: STATUS_BORDER[status] }} />
                <span>{label}</span>
                <span className="font-semibold text-gray-800 ml-0.5">{statusCounts[status]}</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── Hall tabs ──────────────────────────────────────────────────── */}
      {data.halls?.length > 1 && (
        <div className="bg-white border-b border-gray-100 px-6 flex gap-1 overflow-x-auto">
          {data.halls.map(h => (
            <button
              key={h.id}
              onClick={() => setSelectedHall(h.id)}
              className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${selectedHall === h.id ? 'border-teal-500 text-teal-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
              data-testid={`hall-tab-${h.id}`}
            >
              {h.name}
            </button>
          ))}
        </div>
      )}

      {/* ── Main layout ────────────────────────────────────────────────── */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── Left panel ─────────────────────────────────────────────── */}
        <div className={`bg-white border-r border-gray-200 flex flex-col transition-all duration-200 ${panelOpen ? 'w-64' : 'w-10'} shrink-0`}>
          <button
            onClick={() => setPanelOpen(v => !v)}
            className="flex items-center justify-between px-3 py-2.5 border-b border-gray-100 text-xs text-gray-500 hover:bg-gray-50"
            data-testid="panel-toggle"
          >
            {panelOpen ? (
              <>
                <span className="font-semibold text-gray-700 flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" /> Katılımcılar ({filteredParticipants.length})
                </span>
                <ChevronLeft className="w-3.5 h-3.5" />
              </>
            ) : <ChevronRight className="w-3.5 h-3.5 mx-auto" />}
          </button>

          {panelOpen && (
            <>
              <div className="px-3 py-2 border-b border-gray-100">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <input
                    value={participantSearch}
                    onChange={e => setParticipantSearch(e.target.value)}
                    placeholder="Firma ara..."
                    className="w-full h-7 pl-7 pr-2 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-400"
                    data-testid="participant-search"
                  />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                {filteredParticipants.length === 0 ? (
                  <div className="py-8 text-center text-xs text-gray-400">
                    {participantSearch ? 'Firma bulunamadı' : 'Henüz katılımcı yok'}
                  </div>
                ) : (
                  filteredParticipants.map((p, i) => (
                    <button
                      key={`${p.stand_id}-${i}`}
                      onClick={() => handleParticipantClick(p)}
                      className={`w-full text-left px-3 py-2 flex items-center gap-2.5 hover:bg-gray-50 border-b border-gray-50 transition-colors ${highlightedStandId === p.stand_id ? 'bg-teal-50' : ''}`}
                      data-testid={`participant-${i}`}
                    >
                      <div className={`w-6 h-6 rounded flex items-center justify-center shrink-0 ${p.status === 'satıldı' ? 'bg-red-100' : 'bg-amber-100'}`}>
                        <Building2 className={`w-3 h-3 ${p.status === 'satıldı' ? 'text-red-600' : 'text-amber-600'}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-gray-800 truncate">{p.company_name}</p>
                        <p className="text-[10px] text-gray-400">
                          Stand {p.stand_number}
                          {p.width && p.height ? ` · ${p.width * p.height}m²` : ''}
                        </p>
                      </div>
                      <span className={`text-[9px] px-1 py-0.5 rounded border ${STATUS_BADGE_CLS[p.status] || ''} shrink-0`}>
                        {STATUS_LABEL[p.status] || p.status}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </div>

        {/* ── Canvas area ────────────────────────────────────────────── */}
        <div className="flex-1 relative overflow-hidden bg-gray-100"
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          onWheel={onWheel}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          {/* Zoom controls */}
          <div className="absolute top-4 right-4 z-20 flex flex-col gap-1.5">
            <button onClick={() => setScale(s => Math.min(4, s * 1.2))} className="w-8 h-8 bg-white border border-gray-200 rounded-lg flex items-center justify-center hover:bg-gray-50 shadow-sm" data-testid="zoom-in"><ZoomIn className="w-4 h-4 text-gray-600" /></button>
            <button onClick={() => setScale(s => Math.max(0.3, s * 0.8))} className="w-8 h-8 bg-white border border-gray-200 rounded-lg flex items-center justify-center hover:bg-gray-50 shadow-sm" data-testid="zoom-out"><ZoomOut className="w-4 h-4 text-gray-600" /></button>
            <button onClick={() => { setScale(1); setOffset({ x: 0, y: 0 }); }} className="w-8 h-8 bg-white border border-gray-200 rounded-lg flex items-center justify-center hover:bg-gray-50 shadow-sm" data-testid="reset-zoom"><RotateCcw className="w-3.5 h-3.5 text-gray-600" /></button>
          </div>

          {/* Stand count */}
          <div className="absolute top-4 left-4 z-20 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1.5 text-xs text-gray-600 border border-gray-200 shadow-sm">
            {statusCounts.müsait} stand müsait
          </div>

          {/* SVG Canvas */}
          <div style={{ transform: `translate(${offset.x}px, ${offset.y}px)`, transformOrigin: '0 0', userSelect: 'none' }}>
            <svg
              ref={svgRef}
              width={canvasW * scale + 80}
              height={canvasH * scale + 80}
              style={{ display: 'block' }}
              data-testid="expo-svg-canvas"
            >
              <defs>
                <pattern id="grid" width={CELL_SIZE * scale} height={CELL_SIZE * scale} patternUnits="userSpaceOnUse" x={40} y={40}>
                  <path d={`M ${CELL_SIZE * scale} 0 L 0 0 0 ${CELL_SIZE * scale}`} fill="none" stroke="#e5e7eb" strokeWidth="0.5" />
                </pattern>
              </defs>
              <rect x={0} y={0} width="100%" height="100%" fill="white" />
              <rect x={40} y={40} width={canvasW * scale} height={canvasH * scale} fill="url(#grid)" />

              <g transform={`translate(40, 40)`}>
                {hallStands.map(stand => (
                  <StandShape
                    key={stand.id}
                    stand={stand}
                    scale={scale}
                    onClick={handleStandClick}
                    highlighted={highlightedStandId === stand.id}
                  />
                ))}
              </g>
            </svg>
          </div>

          {hallStands.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-400 text-sm">Bu salon için henüz stand eklenmemiş</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Participant Detail Modal ─────────────────────────────────── */}
      <Dialog open={!!selectedParticipant} onOpenChange={() => { setSelectedParticipant(null); setHighlightedStandId(null); }}>
        <DialogContent className="max-w-sm" data-testid="participant-detail-modal">
          {selectedParticipant && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-teal-600" />
                  {selectedParticipant.company_name}
                </DialogTitle>
                {selectedParticipant.full_name && selectedParticipant.full_name !== selectedParticipant.company_name && (
                  <p className="text-xs text-gray-500 mt-0.5">{selectedParticipant.full_name}</p>
                )}
              </DialogHeader>
              <div className="space-y-3 pt-1 max-h-[60vh] overflow-y-auto pr-1">
                {/* Stand bilgileri */}
                <div>
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Stand Bilgileri</p>
                  <div className="p-3 bg-slate-50 rounded-lg space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Stand No</span>
                      <span className="font-bold text-gray-900">{selectedParticipant.stand_number || '—'}</span>
                    </div>
                    {selectedParticipant.width && selectedParticipant.height ? (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Alan</span>
                        <span className="font-semibold text-gray-900">{selectedParticipant.width * selectedParticipant.height} m²</span>
                      </div>
                    ) : null}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Durum</span>
                      <Badge className={`text-xs border ${STATUS_BADGE_CLS[selectedParticipant.status] || 'bg-gray-100'}`}>
                        {STATUS_LABEL[selectedParticipant.status] || selectedParticipant.status}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Fuar</span>
                      <span className="font-medium text-gray-800 truncate max-w-[160px]">{ex.name}</span>
                    </div>
                  </div>
                </div>

                {/* Firma iletişim bilgileri */}
                {(selectedParticipant.email || selectedParticipant.phone || selectedParticipant.address) && (
                  <div>
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Firma Bilgileri</p>
                    <div className="p-3 bg-slate-50 rounded-lg space-y-2">
                      {selectedParticipant.email && (
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                          <a href={`mailto:${selectedParticipant.email}`} className="text-teal-600 hover:underline truncate">{selectedParticipant.email}</a>
                        </div>
                      )}
                      {selectedParticipant.phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                          <span className="text-gray-700">{selectedParticipant.phone}</span>
                        </div>
                      )}
                      {selectedParticipant.address && (
                        <div className="flex items-start gap-2 text-sm">
                          <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-0.5" />
                          <span className="text-gray-700">
                            {selectedParticipant.address}
                            {(selectedParticipant.city || selectedParticipant.country) && (
                              <span className="text-gray-400"> · {[selectedParticipant.city, selectedParticipant.country].filter(Boolean).join(', ')}</span>
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Yetkili kişiler */}
                {selectedParticipant.contacts?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Yetkili Kişiler</p>
                    <div className="space-y-2">
                      {selectedParticipant.contacts.map((c, i) => (
                        <div key={i} className="p-2.5 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2 mb-1">
                            <User className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            <span className="text-sm font-medium text-gray-800">{c.name}</span>
                            {c.title && <span className="text-xs text-gray-400">· {c.title}</span>}
                          </div>
                          {c.email && (
                            <div className="flex items-center gap-1.5 text-xs text-gray-500 ml-5">
                              <Mail className="w-3 h-3 text-gray-300 shrink-0" />
                              <a href={`mailto:${c.email}`} className="text-teal-600 hover:underline truncate">{c.email}</a>
                            </div>
                          )}
                          {c.phone_number && (
                            <div className="flex items-center gap-1.5 text-xs text-gray-500 ml-5 mt-0.5">
                              <Phone className="w-3 h-3 text-gray-300 shrink-0" />
                              <span>{c.phone_country_code} {c.phone_number}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <Button
                variant="outline"
                className="w-full mt-2"
                onClick={() => { setSelectedParticipant(null); setHighlightedStandId(null); }}
              >
                Kapat
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Stand Request Modal ──────────────────────────────────────── */}
      <Dialog open={!!requestStand} onOpenChange={() => setRequestStand(null)}>
        <DialogContent className="max-w-md" data-testid="stand-request-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-teal-600" />
              Stand Talep Et — {requestStand?.stand_number}
            </DialogTitle>
            <DialogDescription>
              Bu stand için talebinizi gönderin. Organizatör onayladıktan sonra size bilgi verilecektir.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Ad Soyad *</Label>
                <div className="relative">
                  <User className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <Input value={requestForm.contact_name} onChange={e => setRequestForm(f => ({ ...f, contact_name: e.target.value }))}
                    placeholder="Adınız Soyadınız" className="pl-8 h-9 text-sm" data-testid="req-name" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Firma Adı *</Label>
                <div className="relative">
                  <Building2 className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <Input value={requestForm.contact_company} onChange={e => setRequestForm(f => ({ ...f, contact_company: e.target.value }))}
                    placeholder="Firma Adı" className="pl-8 h-9 text-sm" data-testid="req-company" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">E-posta *</Label>
                <div className="relative">
                  <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <Input type="email" value={requestForm.contact_email} onChange={e => setRequestForm(f => ({ ...f, contact_email: e.target.value }))}
                    placeholder="email@domain.com" className="pl-8 h-9 text-sm" data-testid="req-email" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Telefon</Label>
                <div className="relative">
                  <Phone className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <Input value={requestForm.contact_phone} onChange={e => setRequestForm(f => ({ ...f, contact_phone: e.target.value }))}
                    placeholder="+90 5xx xxx xx xx" className="pl-8 h-9 text-sm" data-testid="req-phone" />
                </div>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Notlar</Label>
              <Textarea value={requestForm.notes} onChange={e => setRequestForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Talep notları, özel istekler..." rows={3} className="text-sm resize-none" data-testid="req-notes" />
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" onClick={() => setRequestStand(null)} className="flex-1">İptal</Button>
              <Button onClick={handleSubmitRequest} disabled={submitting} className="flex-1 bg-teal-600 hover:bg-teal-700" data-testid="req-submit-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
                Talep Gönder
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
